// Orders Luijten VVZ — mirror van useOrdersDecoprof maar gefilterd op
// regels waarvan de leverancier WEL in ORDERS_DECOPROF_EXCLUDED_SUPPLIERS staat
// (= Luijten-VVZ BV / Delta Paints BV → "Luijten VVZ" stroom).
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { ORDERS_DECOPROF_EXCLUDED_SUPPLIERS, type OrderDecoprofStatus } from "@/lib/ordersDecoprof";

export interface OrdersLuijtenRow {
  ordernumber: string;
  orderid: number | null;
  primarySupplier: string | null;
  suppliers: string[];
  status: OrderDecoprofStatus;
  createdAt: string | null;
}

interface RawRow {
  orderid: number | null;
  ordernumber: string | null;
  suppliertitle: string | null;
  producttitle: string | null;
  orders_decoprof_status: string | null;
  createdat: string | null;
  status: string | null;
}

const LIGHTSPEED_COMPLETED_STATUSES = [
  "packed_completed",
  "completed_picked_up",
  "completed_shipped",
  "cancelled",
  "canceled",
  "Geannuleerd",
] as const;

const QUERY_KEY = ["orders-luijten-vvz"] as const;
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
const MAX_VISIBLE_ORDERS = 2000;

const createdAtMs = (v: string | null) => {
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
};
const compareDesc = (a: OrdersLuijtenRow, b: OrdersLuijtenRow) =>
  createdAtMs(b.createdAt) - createdAtMs(a.createdAt);

export const useOrdersLuijtenVvz = () => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();
  const qc = useQueryClient();
  const sinceIsoRef = useRef(new Date(Date.now() - SIXTY_DAYS_MS).toISOString());

  const query = useQuery({
    queryKey: QUERY_KEY,
    staleTime: 30_000,
    enabled: isAuthenticated && isSessionValid && !isValidating,
    queryFn: async (): Promise<OrdersLuijtenRow[]> => {
      const includedList = Array.from(ORDERS_DECOPROF_EXCLUDED_SUPPLIERS);
      const includedCsv = `(${includedList.map((s) => `"${s}"`).join(",")})`;
      const completedCsv = `(${LIGHTSPEED_COMPLETED_STATUSES.map((s) => `"${s}"`).join(",")})`;

      const { data, error } = await supabase
        .from("orders_live_ls")
        .select("orderid, ordernumber, suppliertitle, producttitle, orders_decoprof_status, createdat, status")
        .gte("createdat", sinceIsoRef.current)
        .not("suppliertitle", "is", null)
        .neq("suppliertitle", "")
        .neq("producttitle", "No products")
        .in("suppliertitle", includedList)
        .not("status", "in", completedCsv)
        .order("createdat", { ascending: false })
        .limit(MAX_VISIBLE_ORDERS);

      if (error) {
        console.error("[useOrdersLuijtenVvz] Error:", error);
        throw error;
      }
      return aggregateRows((data ?? []) as RawRow[]);
    },
  });

  useEffect(() => {
    if (!isAuthenticated || !isSessionValid || isValidating) return;
    const channel = supabase
      .channel("orders-luijten-vvz-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders_live_ls" },
        () => {
          qc.invalidateQueries({ queryKey: QUERY_KEY });
        }
      )
      .subscribe();
    return () => {
      try { supabase.removeChannel(channel); } catch { /* noop */ }
    };
  }, [isAuthenticated, isSessionValid, isValidating, qc]);

  return query;
};

function aggregateRows(rows: RawRow[]): OrdersLuijtenRow[] {
  const byOrder = new Map<string, {
    orderid: number | null;
    suppliers: Set<string>;
    primary: string | null;
    status: OrderDecoprofStatus;
    createdAt: string | null;
  }>();
  for (const row of rows) {
    if (!row.ordernumber) continue;
    const supplier = row.suppliertitle?.trim() || null;
    let entry = byOrder.get(row.ordernumber);
    if (!entry) {
      entry = {
        orderid: row.orderid ?? null,
        suppliers: new Set<string>(),
        primary: null,
        status: (row.orders_decoprof_status as OrderDecoprofStatus) || "Nieuw",
        createdAt: row.createdat ?? null,
      };
      byOrder.set(row.ordernumber, entry);
    } else if (entry.orderid == null && row.orderid != null) {
      entry.orderid = row.orderid;
    }
    if (supplier) {
      entry.suppliers.add(supplier);
      if (!entry.primary) entry.primary = supplier;
    }
  }
  const result: OrdersLuijtenRow[] = [];
  for (const [ordernumber, entry] of byOrder.entries()) {
    result.push({
      ordernumber,
      orderid: entry.orderid,
      primarySupplier: entry.primary,
      suppliers: Array.from(entry.suppliers),
      status: entry.status,
      createdAt: entry.createdAt,
    });
  }
  return result.sort(compareDesc);
}
